import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Check, X, Building2, Loader2, ClipboardCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { pending: "In afwachting", approved: "Goedgekeurd", rejected: "Afgewezen" };
const STATUS_KLEUR = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

function AdresBlok({ label, adres, huidig }) {
  const gewijzigd = (a, b, k) => (a?.[k] || "").trim() !== (b?.[k] || "").trim();
  return (
    <div className="border border-border rounded-lg p-3 bg-secondary/20">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
        <MapPin className="h-3 w-3" /> {label}
      </div>
      {adres ? (
        <div className="text-sm leading-tight mt-1.5 space-y-0.5">
          {["straat", "postcode", "plaats", "land"].map((k) => {
            const veld = adres[k] || "—";
            const anders = huidig && gewijzigd(adres, huidig, k);
            return (
              <div key={k} className={anders ? "text-emerald-700 font-medium" : ""}>
                {k === "postcode" ? `${adres.postcode || ""} ${adres.plaats || ""}` : k === "plaats" ? null : veld}
              </div>
            );
          }).filter(Boolean)}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic mt-1">Niet opgegeven</div>
      )}
    </div>
  );
}

export default function ShopAdresWijzigingen() {
  const [tab, setTab] = useState("pending");
  const [rijen, setRijen] = useState([]);
  const [orgs, setOrgs] = useState({}); // id → org-obj
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shop/adres-wijzigingen", { params: { status: tab } });
      const lijst = data || [];
      setRijen(lijst);
      // Haal organisaties op voor huidig adres + naam
      const ids = [...new Set(lijst.map((r) => r.organisatie_id).filter(Boolean))];
      const nieuw = { ...orgs };
      await Promise.all(ids.map(async (id) => {
        if (!nieuw[id]) {
          try {
            const { data: o } = await api.get(`/organisaties/${id}`);
            nieuw[id] = o;
          } catch { /* silent */ }
        }
      }));
      setOrgs(nieuw);
    } catch { toast.error("Laden mislukt"); }
    finally { setLoading(false); }
  }, [tab]);  // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Adreswijziging goedkeuren en direct op de organisatie zetten?")) return;
    setBusyId(id);
    try {
      await api.post(`/shop/adres-wijzigingen/${id}/approve`, {});
      toast.success("Goedgekeurd — nieuwe adressen actief");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Goedkeuren mislukt"); }
    setBusyId(null);
  };
  const reject = async (id) => {
    if (!window.confirm("Adreswijziging afwijzen?")) return;
    setBusyId(id);
    try {
      await api.post(`/shop/adres-wijzigingen/${id}/reject`, {});
      toast.success("Afgewezen");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Afwijzen mislukt"); }
    setBusyId(null);
  };

  return (
    <div className="space-y-6" data-testid="adres-wijzigingen-page">
      <div>
        <div className="text-sm text-muted-foreground flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 text-accent" /> Webshop
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Adres-wijzigingen</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Adres-wijzigingsverzoeken vanuit de webshop. Vergelijk het huidige adres op de organisatie met het gevraagde adres, en keur goed of wijs af.
        </p>
      </div>

      <div className="flex gap-1.5">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`tab-${s}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tab === s ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin inline" />
        </div>
      ) : rijen.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-sm text-muted-foreground" data-testid="wijzigingen-leeg">
          Geen wijzigingsverzoeken in deze categorie.
        </div>
      ) : (
        <div className="space-y-3">
          {rijen.map((r) => {
            const org = orgs[r.organisatie_id];
            const huidigBezorg = org?.verzendadres && Object.keys(org.verzendadres).length ? org.verzendadres : {
              straat: org?.straat, postcode: org?.postcode, plaats: org?.plaats, land: org?.land,
            };
            const huidigFactuur = org?.factuuradres && Object.keys(org.factuuradres).length ? org.factuuradres : {
              straat: org?.straat, postcode: org?.postcode, plaats: org?.plaats, land: org?.land,
            };
            return (
              <div key={r.id} className="bg-card border border-border rounded-xl p-4" data-testid={`wijziging-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <a href={`/organisaties/${r.organisatie_id}`}
                         className="font-medium hover:underline truncate">
                        {org?.naam || r.organisatie_id}
                      </a>
                      <Badge variant="outline" className={STATUS_KLEUR[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(r.created_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                      {org?.klantcode && <> · Klantcode <span className="font-mono">{org.klantcode}</span></>}
                    </div>
                  </div>
                  {r.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => approve(r.id)} disabled={busyId === r.id} data-testid={`approve-${r.id}`}>
                        {busyId === r.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                        Goedkeuren
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(r.id)} disabled={busyId === r.id} data-testid={`reject-${r.id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid gap-3 mt-4 md:grid-cols-2">
                  {/* Bezorgadres huidig → nieuw */}
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] items-center">
                    <AdresBlok label="Huidig bezorgadres" adres={huidigBezorg} huidig={r.bezorgadres} />
                    <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto sm:mx-1" />
                    <AdresBlok label="Nieuw bezorgadres" adres={r.bezorgadres} huidig={huidigBezorg} />
                  </div>
                  {/* Factuuradres huidig → nieuw */}
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] items-center">
                    <AdresBlok label="Huidig factuuradres" adres={huidigFactuur} huidig={r.factuuradres} />
                    <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto sm:mx-1" />
                    <AdresBlok label="Nieuw factuuradres" adres={r.factuuradres} huidig={huidigFactuur} />
                  </div>
                </div>

                {(r.goedgekeurd_op || r.afgewezen_op) && (
                  <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                    {r.goedgekeurd_op && <span>Goedgekeurd op {new Date(r.goedgekeurd_op).toLocaleString("nl-NL")}</span>}
                    {r.afgewezen_op && <span>Afgewezen op {new Date(r.afgewezen_op).toLocaleString("nl-NL")}</span>}
                    {r.goedgekeurd_door && <span>· door {r.goedgekeurd_door}</span>}
                    {r.afgewezen_door && <span>· door {r.afgewezen_door}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
